import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuthStore } from "../../stores/authStore";
import { client } from "../../lib/api";
import Input from "../../components/Input";

export default function SignupPage() {
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get("invite");

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [invitationMessage, setInvitationMessage] = useState("");
  const [trainerId, setTrainerId] = useState<string | null>(null);
  const { signUp } = useAuthStore();

  useEffect(() => {
    const decodeInvite = async () => {
      if (inviteCode) {
        try {
          const result = await client.invitations.getInvitations({
            code: inviteCode,
          });
          setEmail(result.email);
          setTrainerId(result.trainerId);
          setInvitationMessage("You've been invited by your trainer!");
        } catch (err) {
          setError("Invalid invitation code");
        }
      }
    };
    decodeInvite();
  }, [inviteCode]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      await signUp(email, password, name);

      // If trainerId is present, link the new user to the trainer
      if (trainerId) {
        try {
          await client.authorization.postAuthorizationLinkTrainer({
            requestBody: { trainerId },
          });
        } catch (linkError) {
          console.error("Failed to link trainer:", linkError);
          // Don't fail signup if linking fails - trainer can re-send invite
        }
      }

      setSuccess(true);
    } catch (err: any) {
      if (err.response) {
        // Any server-side rejection (including "email already in use") is treated
        // the same as success to prevent account enumeration.
        setSuccess(true);
      } else {
        // Only surface a generic error for network/connectivity failures.
        setError("Something went wrong. Please check your connection and try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bg-secondary py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-12 w-12 text-green-600 mb-4">
              <svg
                className="h-12 w-12"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                />
              </svg>
            </div>
            <h2 className="text-3xl font-extrabold text-text-primary mb-4">
              Check your email
            </h2>
            <p className="text-gray-600 mb-2">
              We've sent a verification link to{" "}
              <span className="font-medium text-text-primary">{email}</span>.
            </p>
            <p className="text-gray-600 mb-8">
              Please check your inbox and click the link to verify your account
              before signing in. If you don't receive an email, you may already
              have an account —{" "}
              <Link to="/login" className="text-indigo-600 hover:text-indigo-500 underline">
                try signing in
              </Link>{" "}
              or{" "}
              <Link
                to="/forgot-password"
                className="text-indigo-600 hover:text-indigo-500 underline"
              >
                reset your password
              </Link>
              .
            </p>
            <Link
              to="/login"
              className="inline-flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Go to sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg-secondary py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-text-primary">
            Create your account
          </h2>
          {invitationMessage && (
            <div className="mt-4 rounded-md bg-blue-50 p-4">
              <p className="text-sm text-blue-800 text-center">
                {invitationMessage}
              </p>
            </div>
          )}
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="name" className="sr-only">
                Name
              </label>
              <Input
                id="name"
                name="name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                variant="top"
                placeholder="Full name"
              />
            </div>
            <div>
              <label htmlFor="email-address" className="sr-only">
                Email address
              </label>
              <Input
                id="email-address"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={!!inviteCode}
                variant="middle"
                placeholder="Email address"
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Password
              </label>
              <Input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                variant="bottom"
                placeholder="Password (8+ characters)"
                minLength={8}
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
            >
              {loading ? "Creating account..." : "Sign up"}
            </button>
          </div>

          <div className="text-center">
            <Link to="/login" className="text-indigo-600 hover:text-indigo-500">
              Already have an account? Sign in
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
